import { TFileUploadRequest } from '@gen2/api/files/api';
import { useUploadFileMutation } from '@gen2/api/files/hooks';
import { FollowUpKeys } from '@gen2/api/follow-up/hooks';
import { InviteListingKeys } from '@gen2/api/invite-listing/hooks';
import { InviteRequestsKeys } from '@gen2/api/invite-requests/hooks';
import { TemplateKeys } from '@gen2/api/templates/hooks';
import { queryClient } from '@gen2/config';
import { useToast } from '@gen2/hooks';
import { useTranslation } from 'react-i18next';
import { ValidationError } from 'yup';
import { TRequestContainer } from '../components/invite-request/hooks/request';
import { fileValidationSchema, MAX_FILE_COUNT, MAX_FILE_COUNT_ON_BEHALF } from '../components/invite-request/variant/file-upload/schema';
import { useFollowUpStore } from '../follow-up/store';
import { useSendInviteStore } from '../invites/send-invites/store';
import { useTemplateFormStore } from '../templates/template-form/store';

interface UseFileUploadProps {
  container?: TRequestContainer;
  requestId?: string;
  existingFilesCount?: number;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  onSettled?: () => void;
}

export const useFileUpload = ({
  container = 'invite',
  requestId,
  existingFilesCount = 0,
  onSuccess,
  onError,
  onSettled,
}: UseFileUploadProps = {}) => {
  const { mutate: fileUploadMutation } = useUploadFileMutation();
  const { t } = useTranslation('sendInvite');
  const toast = useToast();
  const siStore = useSendInviteStore();
  const followUp = useFollowUpStore();
  const template = useTemplateFormStore();

  const validateFiles = async (files: File[], contactId?: string): Promise<ValidationError | null> => {
    const totalCount = existingFilesCount + (files?.length || 0);
    let errors: ValidationError | null = null;

    if (contactId ? totalCount > MAX_FILE_COUNT_ON_BEHALF : totalCount > MAX_FILE_COUNT) {
      const upgradeModal = {
        isOpen: true,
        title: t('request.fileUpload.upgrade.title') ?? '',
        description: t('request.fileUpload.upgrade.description') ?? '',
      };

      if (container === 'invite') {
        siStore.setUpgradeModal(upgradeModal);
      }

      if (container === 'follow-up') {
        followUp.setUpgradeModal(upgradeModal);
      }

      if (container === 'template') {
        template.setUpgradeModal(upgradeModal);
      }

      errors = new ValidationError(
        'request.fileUpload.file.max',
        'file',
        'file'
      );
    } else {
      try {
        await Promise.all(
          files.map(async (file) => {
            await fileValidationSchema.validate(
              { file },
              { abortEarly: false },
            );
          }),
        );
      } catch (err: unknown) {
        if (err instanceof ValidationError) {
          errors = err;
        }
      }
    }

    return errors;
  };

  const uploadFiles = async (files: File[], contactId?: string): Promise<void> => {
    if (!requestId) return;

    const successMessage = files.length > 1 ? 'uploaded_multiple' : 'uploaded';

    files.forEach(async (file, index) => {
      const payload: TFileUploadRequest = {
        type: 'request',
        typeId: requestId,
        file,
      };

      if (contactId) {
        payload.type = 'on-behalf';
        payload.contactId = contactId;
      }

      fileUploadMutation(
        payload,
        {
          onSuccess: () => {
            if (index === files.length - 1) {
              toast.show({
                text: t(`request.fileUpload.message.${successMessage}`) ?? '',
                variant: 'success',
              });

              if (container === 'invite') {
                queryClient.invalidateQueries([InviteRequestsKeys.getInviteRequests]);
                queryClient.invalidateQueries([InviteRequestsKeys.getInviteRequestGroups]);
                queryClient.invalidateQueries([InviteListingKeys.queryInvitesRequests]);
                queryClient.invalidateQueries([FollowUpKeys.getFollowUps]);
              }

              if (container === 'follow-up') {
                queryClient.invalidateQueries([FollowUpKeys.getFollowUp]);
                queryClient.invalidateQueries([FollowUpKeys.getFollowUps]);
              }

              if (container === 'template') {
                queryClient.invalidateQueries([TemplateKeys.getTemplateRequests]);
                queryClient.invalidateQueries([
                  TemplateKeys.getTemplateRequestGroups,
                ]);
              }

              onSuccess?.();
            }
          },
          onError: (err: any) => {
            toast.show({
              variant: 'error',
              text: err.data.message,
            });
            onError?.(err);
          },
          onSettled: () => {
            onSettled?.();
          },
        },
      );
    });
  };

  const handleFileUpload = async (files: File[], contactId?: string): Promise<ValidationError | null> => {
    const errors = await validateFiles(files, contactId);

    if (!errors) {
      await uploadFiles(files, contactId);
    }

    return errors;
  };

  return {
    handleFileUpload,
    validateFiles,
  };
};